import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listShiftHistory } from '../../../../api/shifts-client';
import { listPortfolioBuildings } from '../../../../api/hierarchy-client';
import {
  ShiftHistoryResponse,
  ShiftHistoryRow,
} from '../../../../interfaces/shift-interfaces';
import { PortfolioBuildingIndexRow } from '../../../../interfaces/hierarchy-interfaces';
import {
  clockDayTime,
  clockTime,
  zoneLabel,
  zonedDayEndIso,
  zonedDayStartIso,
} from '../../../../function-utils/shift-time';
import { ShiftMonitorPanel } from '../../shifts/shift-monitor-panel';
import { useClock } from '../../../../context/clock-context';
import { ClockNote } from '../../../clock-note/clock-note';
import { ShiftDetailDrawer } from './shift-detail-drawer';
import {
  IndicatorGroup,
  listItemMotion,
  m,
  Presence,
  StateSwitch,
} from '../../../../motion';
import {
  Chip,
  DateInput,
  EmptyNote,
  EmptyPanel,
  EmptyTitle,
  ErrorPanel,
  Field,
  FilterBar,
  HistoryStack,
  Pager,
  ResetButton,
  ResultLine,
  RowMono,
  RowPrimary,
  RowSecondary,
  RowStack,
  Select,
  ShiftList,
  ShiftRow,
  ShiftsWrap,
  SkeletonRow,
  SummaryBar,
  SummaryNote,
  ZoneCaption,
  TagRow,
  ToggleRow,
  ViewSwitch,
  ViewTab,
  ViewTabIndicator,
  ViewTabLabel,
} from './dashboard-shifts.styled';

const INDICATOR_ID = 'shift-views';

/* A row arriving: capped cascade, keyed by shift id, NO layout animation —
   the page is 25 rows and a page change replaces every id, which is exactly
   when the cascade should replay. */
const rowMotion = (index: number) => ({ ...listItemMotion(index), layout: false });

/** A list item that can carry the row variants. */
const ShiftItem = m.li``;

/**
 * THE SHIFTS TAB — owner and property manager only.
 *
 * Owner instruction, 2026-08-11: "a new tab shifts which will give me an
 * overview of all shifts by specific building or portfolio and what happened on
 * them when i click on a shift."
 *
 * ═══════════════ ONE TAB, TWO VIEWS, AND THEY ANSWER DIFFERENT THINGS ═══════
 *   LIVE      who is on the desk right now, who is logged in and has not clocked
 *             in. A snapshot nobody keeps.
 *   HISTORY   every past shift, what it was, and what happened on it. A record
 *             somebody will quote back at a person.
 *
 * The live view is the /shift-monitor page's own panel, mounted here rather than
 * copied: its three groups carry caveats that took a written decision to word
 * ("an assignment list, not a headcount"), and a second copy of them is how one
 * of the two screens quietly loses the caveat. /shift-monitor now redirects
 * here, so there is one place in the product that says "shifts".
 *
 * ═══════════════ THE FILTERS DO NOT AUTHORISE ANYTHING ═══════════════
 * `shifts` and `properties` are both RLS-narrowed by app_sees_building(), so a
 * manager scoped to three buildings gets three buildings' shifts whether or not
 * they pick one. The building and portfolio selects are a convenience over an
 * answer that is already correct — which is also why the building list here is
 * whatever the server hands back, never filtered again in JS.
 */

const COPY = {
  live: 'On now',
  history: 'History',
  portfolio: 'Portfolio',
  building: 'Building',
  from: 'From',
  to: 'To',
  allPortfolios: 'All portfolios',
  allBuildings: 'All buildings',
  openOnly: 'Only shifts still running',
  reset: 'Clear filters',
  loading: 'Reading the shifts…',
  failed: 'Could not read the shift history.',
  emptyTitle: 'No shifts here',
  emptyNote:
    'Nothing matches these filters. Widen the dates, or clear them to see every desk you cover.',
  emptyAll: 'No shifts have been recorded at the buildings you cover yet.',
  prev: 'Previous',
  next: 'Next',
  handedOver: 'Handed over',
  noHandover: 'No handover',
  pending: 'Waiting for the next shift',
  unread: 'Not opened',
  opened: 'Opened',
  onNow: 'On now',
  retro: 'Closed later',
  floor: 'No written summary',
  summaryTitle: 'Across every shift these filters match',
  summaryPaged:
    'These counts cover the whole filtered result, not just the page below.',
};

const PAGE_SIZE = 25;

const minutesLabel = (mins: number): string => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m}m`;

  return m ? `${h}h ${m}m` : `${h}h`;
};

// The DATE INPUT gives a local calendar day; the API wants an instant. `to` is
// pushed to the END of the chosen day, because a manager who types 11 August
// means "including the 11th" and a naive midnight-to-midnight range silently
// drops everything that happened that day.
//
// WHICH CLOCK THAT CALENDAR DAY BELONGS TO: the same one every row below is
// shown on — the reader's own (R-T12; see `clock` in the component). The
// boundaries are computed with the zone passed EXPLICITLY (zonedDayStartIso /
// zonedDayEndIso), and the screen PRINTS which clock it is, so the value shown
// and the value computed come from one expression.

// The supervisory pair, as one chip. A handover that nobody has taken and one
// that was taken and never opened are DIFFERENT failures, and a single "unread"
// label would collapse them into a complaint about the wrong person.
const handoffChip = (
  row: ShiftHistoryRow
): { label: string; tone: 'success' | 'warning' | 'neutral' | 'brand' } => {
  if (!row.handoff) return { label: COPY.noHandover, tone: 'neutral' };
  if (row.handoff.pending) return { label: COPY.pending, tone: 'warning' };
  if (!row.handoff.read_at) return { label: COPY.unread, tone: 'warning' };

  return { label: COPY.opened, tone: 'success' };
};

export const DashboardShifts: React.FC = () => {
  const [view, setView] = useState<'live' | 'history'>('history');

  const [buildings, setBuildings] = useState<PortfolioBuildingIndexRow[]>([]);
  const [portfolioId, setPortfolioId] = useState<number | ''>('');
  const [buildingId, setBuildingId] = useState<number | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openOnly, setOpenOnly] = useState(false);
  const [offset, setOffset] = useState(0);

  const [data, setData] = useState<ShiftHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ShiftHistoryRow | null>(null);

  // ONE request for the whole building/portfolio index, which is what the
  // pickers need. Failing is not fatal: the filters degrade to "all", and the
  // list below is already correct without them.
  useEffect(() => {
    let alive = true;
    listPortfolioBuildings()
      .then(rows => {
        if (alive) setBuildings(rows || []);
      })
      .catch(() => {
        if (alive) setBuildings([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  const portfolios = useMemo(() => {
    const seen = new Map<number, string>();
    buildings.forEach(b => {
      if (b.portfolio_id != null && !seen.has(b.portfolio_id)) {
        seen.set(b.portfolio_id, b.portfolio_name || `Portfolio ${b.portfolio_id}`);
      }
    });

    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [buildings]);

  // The building select NARROWS with the portfolio select, so the two can never
  // describe an impossible combination on screen. A building already chosen that
  // is not in the newly chosen portfolio is cleared rather than left showing a
  // name the list no longer contains.
  const visibleBuildings = useMemo(
    () =>
      buildings
        .filter(b => portfolioId === '' || b.portfolio_id === portfolioId)
        .sort((a, b) =>
          (a.property_name || '').localeCompare(b.property_name || '')
        ),
    [buildings, portfolioId]
  );

  // ONE CLOCK FOR THE WHOLE SCREEN — THE READER'S OWN (R-T12).
  //
  // The date filters have meant the reader's own day since the owner's ruling of
  // 2026-08-25. The ROWS did not: each was shown in its own building's zone,
  // with a caption warning that the times "are not directly comparable". Under
  // R-T12 an owner or a manager has one clock across every building they hold —
  // theirs — so the rows are on it too, the caption names it once, and two
  // shifts in two cities can finally be compared by eye. A shift's time on its
  // own building's clock is one click away, in its detail.
  const clock = useClock();
  const filterZone = clock.zone;

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await listShiftHistory({
        buildingId: buildingId === '' ? undefined : buildingId,
        portfolioId: portfolioId === '' ? undefined : portfolioId,
        from: zonedDayStartIso(from, filterZone),
        to: zonedDayEndIso(to, filterZone),
        openOnly,
        limit: PAGE_SIZE,
        offset,
      });
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : COPY.failed);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [buildingId, portfolioId, from, to, openOnly, offset, filterZone]);

  useEffect(() => {
    if (view === 'history') load();
  }, [view, load]);

  // Any filter change returns to page one. Staying on page 4 of a narrower
  // result set shows an empty list that looks like "no shifts" rather than
  // "you are past the end".
  const onFilterChange = (fn: () => void): void => {
    fn();
    setOffset(0);
  };

  const rows = data?.shifts || [];
  const total = data?.total || 0;
  const summary = data?.summary;

  // How many CLOCKS the buildings behind the visible rows are on — for the one
  // sentence that says a manager's clock is not the desk's. Counted by city
  // name, so two buildings in one zone are one.
  const rowZones = useMemo(() => {
    const seen = new Set<string>();
    rows.forEach(row => {
      const named = zoneLabel(row.timezone);
      if (named) seen.add(named);
    });

    return [...seen].sort();
  }, [rows]);
  const hasFilters =
    portfolioId !== '' || buildingId !== '' || !!from || !!to || openOnly;

  const resetFilters = (): void => {
    setPortfolioId('');
    setBuildingId('');
    setFrom('');
    setTo('');
    setOpenOnly(false);
    setOffset(0);
  };

  return (
    <ShiftsWrap>
      <ViewSwitch role="tablist" aria-label="Shift views">
        {/* One pill, moving between the two tabs. */}
        <IndicatorGroup id={INDICATOR_ID}>
          <ViewTab
            type="button"
            role="tab"
            aria-selected={view === 'live'}
            active={view === 'live'}
            onClick={() => setView('live')}
          >
            {view === 'live' ? <ViewTabIndicator id={INDICATOR_ID} /> : null}
            <ViewTabLabel>{COPY.live}</ViewTabLabel>
          </ViewTab>
          <ViewTab
            type="button"
            role="tab"
            aria-selected={view === 'history'}
            active={view === 'history'}
            onClick={() => setView('history')}
          >
            {view === 'history' ? <ViewTabIndicator id={INDICATOR_ID} /> : null}
            <ViewTabLabel>{COPY.history}</ViewTabLabel>
          </ViewTab>
        </IndicatorGroup>
      </ViewSwitch>

      {view === 'live' ? (
        <ShiftMonitorPanel
          buildingId={buildingId === '' ? null : buildingId}
          buildingName={
            visibleBuildings.find(b => b.id === buildingId)
              ?.property_name
          }
          buildingPicker={
            <Field>
              {COPY.building}
              <Select
                value={buildingId}
                onChange={e =>
                  setBuildingId(e.target.value ? Number(e.target.value) : '')
                }
              >
                <option value="">{COPY.allBuildings}</option>
                {visibleBuildings.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.property_name}
                  </option>
                ))}
              </Select>
            </Field>
          }
        />
      ) : (
        <>
          <FilterBar>
            <Field>
              {COPY.portfolio}
              <Select
                value={portfolioId}
                onChange={e =>
                  onFilterChange(() => {
                    const next = e.target.value ? Number(e.target.value) : '';
                    setPortfolioId(next);
                    setBuildingId('');
                  })
                }
              >
                <option value="">{COPY.allPortfolios}</option>
                {portfolios.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field>
              {COPY.building}
              <Select
                value={buildingId}
                onChange={e =>
                  onFilterChange(() =>
                    setBuildingId(e.target.value ? Number(e.target.value) : '')
                  )
                }
              >
                <option value="">{COPY.allBuildings}</option>
                {visibleBuildings.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.property_name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field>
              {COPY.from}
              <DateInput
                type="date"
                value={from}
                max={to || undefined}
                onChange={e => onFilterChange(() => setFrom(e.target.value))}
              />
            </Field>

            <Field>
              {COPY.to}
              <DateInput
                type="date"
                value={to}
                min={from || undefined}
                onChange={e => onFilterChange(() => setTo(e.target.value))}
              />
            </Field>

            <ToggleRow>
              <input
                type="checkbox"
                checked={openOnly}
                onChange={e => onFilterChange(() => setOpenOnly(e.target.checked))}
              />
              {COPY.openOnly}
            </ToggleRow>

            <ResetButton
              type="button"
              disabled={!hasFilters}
              onClick={resetFilters}
            >
              {COPY.reset}
            </ResetButton>
          </FilterBar>

          {/* WHICH CLOCK THE DATES MEAN AND THE TIMES ARE ON — one, and always
              said. It used to be two sentences about two zones (the reader's
              for the filter, each building's for its row). */}
          <ZoneCaption data-testid="shift-zone-caption">
            <ClockNote clock={clock} inline />. The dates above and every time
            below are on it
            {rowZones.length > 1
              ? `, for all ${rowZones.length} time zones these shifts were worked in (${rowZones.join(
                  ', '
                )})`
              : ''}
            . Open a shift to see it on its own building&apos;s clock.
          </ZoneCaption>

          {/* SKELETON → LIST → EMPTY → ERROR is one box with four fills, so the
              swap is a crossfade rather than a jump. A page change keeps the
              list on screen and dims it (Staleable) — never back to skeleton. */}
          <StateSwitch
            state={
              loading && !data
                ? 'loading'
                : error
                  ? 'error'
                  : data && rows.length === 0
                    ? 'empty'
                    : rows.length
                      ? 'ready'
                      : 'idle'
            }
          >
          {error ? <ErrorPanel>{error}</ErrorPanel> : null}

          {loading && !data ? (
            <ShiftList aria-busy="true">
              <li>
                <SkeletonRow />
              </li>
              <li>
                <SkeletonRow />
              </li>
              <li>
                <SkeletonRow />
              </li>
            </ShiftList>
          ) : null}

          {data && rows.length === 0 ? (
            <EmptyPanel>
              <EmptyTitle>{COPY.emptyTitle}</EmptyTitle>
              <EmptyNote>
                {hasFilters ? COPY.emptyNote : COPY.emptyAll}
              </EmptyNote>
            </EmptyPanel>
          ) : null}

          {rows.length ? (
            <HistoryStack stale={loading}>
              {/* The TOTAL, stated. "25 shifts" and "the first 25 of 4,000" are
                  different answers and a supervisor reading the first as the
                  second is the failure this tab exists to prevent. */}
              <ResultLine>
                {`Showing ${offset + 1}–${offset + rows.length} of ${total}`}
              </ResultLine>

              {/* R1's compact summary. RESOLVED SERVER-SIDE over the whole
                  filtered set — the statuses are derived per row below, so a
                  count taken here could only describe this page, while the line
                  directly above says "of 4,000". A summary that quietly meant
                  something narrower than the sentence beside it would be worse
                  than no summary.
                  Zero-valued entries are dropped: "0 with no handover" is noise
                  on a good day, and its absence is not ambiguous next to a
                  stated total. */}
              {summary ? (
                <SummaryBar
                  aria-label={COPY.summaryTitle}
                  data-testid="shift-summary"
                >
                  <SummaryNote>{COPY.summaryPaged}</SummaryNote>
                  <TagRow>
                    {summary.open ? (
                      <Chip tone="brand">{`${summary.open} ${COPY.onNow.toLowerCase()}`}</Chip>
                    ) : null}
                    {summary.pending ? (
                      <Chip tone="warning">{`${summary.pending} waiting for the next shift`}</Chip>
                    ) : null}
                    {summary.unread ? (
                      <Chip tone="warning">{`${summary.unread} not opened`}</Chip>
                    ) : null}
                    {summary.no_handover ? (
                      <Chip tone="neutral">{`${summary.no_handover} with no handover`}</Chip>
                    ) : null}
                    {summary.retro ? (
                      <Chip tone="neutral">{`${summary.retro} closed later`}</Chip>
                    ) : null}
                    {summary.opened ? (
                      <Chip tone="success">{`${summary.opened} opened`}</Chip>
                    ) : null}
                  </TagRow>
                </SummaryBar>
              ) : null}

              <ShiftList>
                {rows.map((row, index) => {
                  const chip = handoffChip(row);

                  return (
                    <ShiftItem key={row.id} {...rowMotion(index)}>
                      <ShiftRow
                        type="button"
                        selected={selected?.id === row.id}
                        onClick={() => setSelected(row)}
                      >
                        <RowStack>
                          <RowPrimary>
                            {row.staff_name || 'Name unavailable'}
                          </RowPrimary>
                          <RowSecondary>
                            {row.staff_role_label || ''}
                          </RowSecondary>
                        </RowStack>

                        <RowStack>
                          <RowSecondary>{row.building_name}</RowSecondary>
                          <RowSecondary>{row.portfolio_name}</RowSecondary>
                        </RowStack>

                        <RowStack>
                          <RowMono>
                            {`${clockDayTime(row.started_at, clock.zone)} → ${
                              row.ended_at
                                ? clockTime(row.ended_at, clock.zone)
                                : '…'
                            }`}
                          </RowMono>
                          <RowSecondary>
                            {minutesLabel(row.duration_minutes)}
                          </RowSecondary>
                        </RowStack>

                        <TagRow>
                          {row.still_open ? (
                            <Chip tone="brand">{COPY.onNow}</Chip>
                          ) : null}
                          {row.ended_retroactively ? (
                            <Chip tone="neutral">{COPY.retro}</Chip>
                          ) : null}
                          {/* A handover that fell back to the deterministic
                              floor is a REAL handover, not a failure — but a
                              supervisor should know the model never wrote it. */}
                          {row.handoff && row.handoff.status === 'floor' ? (
                            <Chip tone="neutral">{COPY.floor}</Chip>
                          ) : null}
                          <Chip tone={chip.tone}>{chip.label}</Chip>
                        </TagRow>
                      </ShiftRow>
                    </ShiftItem>
                  );
                })}
              </ShiftList>

              <Pager>
                <ResetButton
                  type="button"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  {COPY.prev}
                </ResetButton>
                <ResetButton
                  type="button"
                  disabled={offset + rows.length >= total || loading}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                >
                  {COPY.next}
                </ResetButton>
              </Pager>
            </HistoryStack>
          ) : null}
          </StateSwitch>
        </>
      )}

      {/* The drawer slides in from the right and leaves the same way; the
          Presence here is what keeps it mounted for its exit. */}
      <Presence>
        {selected ? (
          <ShiftDetailDrawer
            key="shift-drawer"
            shift={selected}
            onClose={() => setSelected(null)}
            // Closing a running shift from inside the drawer changes the row it
            // was opened from — it stops being open and gains an end time. Without
            // this the list behind would keep claiming "On now" until something
            // else happened to refetch it.
            onChanged={() => {
              void load();
            }}
          />
        ) : null}
      </Presence>
    </ShiftsWrap>
  );
};
